import { useState, useEffect, useCallback } from 'react';
import { 
  getCurrentUser, 
  loginUser, 
  registerUser, 
  setTokens, 
  clearTokens, 
  refreshToken 
} from '../utils/apiUtils';
import { UserInfo, UserLogin, UserRegister, LoginResponse } from '../types/auth';

export const useAuth = () => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Проверяем, есть ли токены при загрузке
  useEffect(() => {
    const initAuth = async () => {
      const accessToken = localStorage.getItem('access_token');
      if (accessToken) {
        try {
          const userData = await getCurrentUser();
          if (userData) {
            setUser(userData);
          } else {
            // Токен недействителен, очищаем localStorage
            clearTokens();
          }
        } catch (err) {
          console.error('Ошибка инициализации аутентификации:', err);
          clearTokens();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (credentials: UserLogin): Promise<LoginResponse> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await loginUser(credentials);
      
      // Сохраняем токены
      setTokens(response.access_token, response.refresh_token);
      
      // Получаем информацию о пользователе
      const userData = await getCurrentUser();
      if (userData) {
        setUser(userData);
      }
      
      return response;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (userData: UserRegister) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await registerUser(userData);
      return response;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
    setError(null);
  }, []);

  const refreshTokens = useCallback(async () => {
    try {
      const success = await refreshToken();
      if (success) {
        // Получаем обновленную информацию о пользователе
        const userData = await getCurrentUser();
        if (userData) {
          setUser(userData);
        }
        return true;
      } else {
        logout();
        return false;
      }
    } catch (err) {
      console.error('Ошибка обновления токенов:', err);
      logout();
      return false;
    }
  }, [logout]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    user,
    loading,
    error,
    login,
    register,
    logout,
    refreshTokens,
    clearError,
    isAuthenticated: !!user
  };
};
